import { Dispatch, useCallback, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

type SetValueAction = () => Generator<any, void, unknown>;

/**
 * Works like `useState` but to set the value, you pass a generator function
 * that can yield multiple values. Each yield updates the state, waits for the
 * next layout effect, and continues the generator. This allows sequential
 * state updates to be written linearly.
 */
export function useYieldState<S>(
  defaultValue: S | (() => S)
): [S, Dispatch<SetValueAction>] {
  const [value, setValue] = useState(defaultValue);
  const effect = useRef(null);

  /**
   * Store the function in a ref so we can always access the current version
   * which has the proper `value` in scope.
   */
  const nextRef = useRef(null);
  nextRef.current = () => {
    // Run the generator to the next yield.
    const newValue = effect.current.next();

    // If the generator is done, reset the effect.
    if (newValue.done) {
      effect.current = null;
      return;
    }

    /**
     * If the value is the same as the current value, then continue to the
     * next yield. Otherwise, set the value in state and wait for the next
     * layout effect.
     */
    if (value === newValue.value) {
      nextRef.current();
    } else {
      setValue(newValue.value);
    }
  };

  useIsomorphicLayoutEffect(() => {
    // If there is an effect currently running, continue to the next yield.
    if (effect.current) {
      nextRef.current();
    }
  });

  const queue = useCallback(
    (fn) => {
      effect.current = fn();
      nextRef.current();
    },
    [effect, nextRef]
  );

  return [value, queue];
}
